class VeiserzTheme {
  constructor() {
    this.root = window.veiserzRoutes?.root || '/';
    this.bindMobileMenu();
    this.bindPredictiveSearch();
    this.bindQuickAdd();
    this.bindProductForms();
    this.bindCollection();
    this.bindRecommendations();
  }

  announce(message) {
    const region = document.querySelector('[data-live-region]');
    if (region) region.textContent = message;
  }

  bindMobileMenu() {
    const drawer = document.querySelector('[data-mobile-drawer]');
    const open = document.querySelector('[data-menu-open]');
    const close = document.querySelector('[data-menu-close]');
    if (!drawer || !open || !close) return;
    const setState = (isOpen) => {
      drawer.toggleAttribute('open', isOpen);
      drawer.setAttribute('aria-hidden', String(!isOpen));
      open.setAttribute('aria-expanded', String(isOpen));
      document.body.classList.toggle('menu-open', isOpen);
      if (isOpen) close.focus(); else open.focus();
    };
    open.addEventListener('click', () => setState(true));
    close.addEventListener('click', () => setState(false));
    drawer.addEventListener('click', (event) => { if (event.target.closest('a')) setState(false); });
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && drawer.hasAttribute('open')) setState(false); });
  }

  bindPredictiveSearch() {
    document.querySelectorAll('[data-predictive-search]').forEach((container) => {
      const input = container.querySelector('input[type="search"]');
      const results = container.querySelector('[data-predictive-results]');
      if (!input || !results) return;
      let timer;
      let controller;
      input.addEventListener('input', () => {
        clearTimeout(timer);
        const term = input.value.trim();
        if (term.length < 2) { results.innerHTML = ''; results.hidden = true; return; }
        timer = setTimeout(async () => {
          controller?.abort(); controller = new AbortController();
          results.hidden = false;
          results.setAttribute('aria-busy', 'true');
          try {
            const url = `${window.veiserzRoutes.predictive}?q=${encodeURIComponent(term)}&resources[type]=product,collection,query&resources[limit]=6&section_id=predictive-search`;
            const response = await fetch(url, { signal: controller.signal });
            const text = await response.text();
            const html = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
            results.innerHTML = html?.innerHTML || '';
          } catch (error) { if (error.name !== 'AbortError') results.innerHTML = ''; }
          results.removeAttribute('aria-busy');
        }, 220);
      });
      input.addEventListener('keydown', (event) => { if (event.key === 'Escape') { results.hidden = true; input.setAttribute('aria-expanded', 'false'); } });
      document.addEventListener('click', (event) => { if (!container.contains(event.target)) results.hidden = true; });
    });
  }

  bindQuickAdd(scope = document) {
    scope.querySelectorAll('[data-quick-add]:not([data-bound])').forEach((form) => {
      form.dataset.bound = 'true';
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const button = form.querySelector('button');
        if (!button) return;
        button.disabled = true;
        try {
          const response = await fetch(window.veiserzRoutes.cartAdd, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
          if (!response.ok) throw new Error('Unable to add item');
          const cart = await fetch(`${window.veiserzRoutes.cart}.js`).then((res) => res.json());
          document.querySelectorAll('[data-cart-count]').forEach((node) => { node.textContent = cart.item_count; });
          this.announce('Added to cart');
        } catch (error) { this.announce(error.message); }
        button.disabled = false;
      });
    });
  }

  bindProductForms() {
    document.querySelectorAll('[data-product-form]').forEach((wrapper) => {
      const script = wrapper.querySelector('[data-product-json]');
      const form = wrapper.querySelector('form');
      if (!script || !form) return;
      const product = JSON.parse(script.textContent);
      const idInput = form.querySelector('[name="id"]');
      const submit = form.querySelector('[type="submit"]');
      const price = wrapper.querySelector('[data-product-price]');
      const compare = wrapper.querySelector('[data-product-compare]');
      const message = wrapper.querySelector('[data-product-message]');
      const moneyFormat = wrapper.dataset.moneyFormat || '${{amount}}';
      const formatMoney = (cents) => moneyFormat.replace(/\{\{\s*amount\s*\}\}/, (cents / 100).toFixed(2));
      const updateVariant = () => {
        const selected = [...form.querySelectorAll('[data-option-select]')].map((select) => select.value);
        const variant = product.variants.find((item) => item.options.every((value, index) => value === selected[index]));
        if (!variant) { submit.disabled = true; message.textContent = 'Unavailable'; return; }
        idInput.value = variant.id;
        submit.disabled = !variant.available;
        submit.querySelector('[data-submit-label]').textContent = variant.available ? submit.dataset.addLabel : submit.dataset.soldLabel;
        price.textContent = formatMoney(variant.price);
        if (compare) { compare.textContent = variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price) : ''; compare.hidden = !compare.textContent; }
        message.textContent = '';
        const url = new URL(window.location.href); url.searchParams.set('variant', variant.id); history.replaceState({}, '', url);
      };
      form.querySelectorAll('[data-option-select]').forEach((select) => select.addEventListener('change', updateVariant));
      form.addEventListener('submit', async (event) => {
        event.preventDefault(); submit.disabled = true; message.textContent = '';
        try {
          const response = await fetch(window.veiserzRoutes.cartAdd, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
          const data = await response.json();
          if (!response.ok) throw new Error(data.description || 'Unable to add item');
          const cart = await fetch(`${window.veiserzRoutes.cart}.js`).then((res) => res.json());
          document.querySelectorAll('[data-cart-count]').forEach((node) => { node.textContent = cart.item_count; });
          message.textContent = 'Added to your bag'; message.dataset.error = 'false';
        } catch (error) { message.textContent = error.message; message.dataset.error = 'true'; }
        submit.disabled = false;
      });
    });
  }

  bindCollection() {
    const section = document.querySelector('[data-collection-section]');
    if (!section) return;
    const filterForm = section.querySelector('[data-filter-form]');
    section.querySelector('[data-filter-toggle]')?.addEventListener('click', () => filterForm?.classList.toggle('is-open'));
    section.querySelector('[data-sort]')?.addEventListener('change', (event) => { const url = new URL(window.location.href); url.searchParams.set('sort_by', event.target.value); window.location.assign(url); });
    const observe = () => {
      const marker = section.querySelector('[data-infinite-marker]');
      if (!marker) return;
      const observer = new IntersectionObserver(async ([entry]) => {
        if (!entry.isIntersecting || marker.dataset.loading === 'true') return;
        marker.dataset.loading = 'true';
        const link = marker.querySelector('a'); if (!link) return;
        link.textContent = link.dataset.loadingLabel;
        try {
          const url = new URL(link.href); url.searchParams.set('section_id', section.dataset.sectionId);
          const html = await fetch(url).then((response) => response.text());
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const incoming = doc.querySelector('[data-product-grid]');
          const nextMarker = doc.querySelector('[data-infinite-marker]');
          incoming?.querySelectorAll('.product-card').forEach((card) => section.querySelector('[data-product-grid]')?.append(card));
          marker.replaceWith(nextMarker || document.createComment('All products loaded'));
          this.bindQuickAdd(section); observe();
        } catch (error) { marker.dataset.loading = 'false'; link.textContent = link.dataset.defaultLabel; }
      }, { rootMargin: '500px 0px' });
      observer.observe(marker);
    };
    observe();
  }

  bindRecommendations() {
    document.querySelectorAll('[data-recommendations-url]').forEach(async (section) => {
      try {
        const html = await fetch(section.dataset.recommendationsUrl).then((response) => response.text());
        const incoming = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-recommendations-url]');
        if (incoming?.innerHTML.trim()) { section.innerHTML = incoming.innerHTML; this.bindQuickAdd(section); }
      } catch (error) {}
    });
  }
}

document.addEventListener('DOMContentLoaded', () => new VeiserzTheme());
